'use client'

import React from 'react'
import { BirhausFinancialDashboard, PropsBirhausFinancialDashboard, MetricaFinanciera } from '../BirhausFinancialDashboard'
import { BirhausBasePropsV3 } from '@birhaus/types/v3'
import { useBirhausThemeV3 } from '@birhaus/themes'

/**
 * BIRHAUS v3.0 Radical Minimalist Financial Dashboard
 * 
 * Enhanced financial dashboard with glass morphism effects,
 * generous spacing, and radical border radius for v3.0 design system.
 * 
 * Features:
 * - Glass morphism metric cards
 * - Generous spacing (2x traditional)
 * - Radical border radius (24px+ curves)
 * - Smooth GPU-accelerated animations
 * - Spanish-first financial terminology
 * - Enhanced readability for financial data
 */

export interface BirhausFinancialDashboardV3Props 
  extends Omit<PropsBirhausFinancialDashboard, 'className'>, Omit<BirhausBasePropsV3, 'variant'> {
  
  /** v3.0 glass intensity preset */
  glassVariant?: 'subtle' | 'medium' | 'heavy'
  
  /** Enable floating metric cards */
  floatingCards?: boolean
  
  /** Enable magnetic hover effects on interactive elements */
  magneticInteractions?: boolean
  
  /** Enable enhanced glass morphism throughout dashboard */
  enableGlass?: boolean
  
  /** Performance optimization mode */
  reducedMotion?: boolean
  
  /** v3 theme variant */
  themeName?: 'financial' | 'healthcare' | 'education'
  
  /** Enable glow effects for critical financial alerts */
  glowAlerts?: boolean
}

export function BirhausFinancialDashboardV3({
  glassVariant = 'medium',
  floatingCards = true,
  magneticInteractions = true,
  enableGlass = true,
  reducedMotion = false,
  themeName = 'financial',
  glowAlerts = true,
  className,
  configuracion = {
    idioma: 'es',
    moneda: 'USD',
    formatoNumero: 'es-PY',
    mostrarGraficos: true,
    mostrarObjetivos: true,
    mostrarProyecciones: true,
    diasProyeccion: 30,
    actualizacionAutomatica: true,
    intervaloActualizacion: 5
  },
  ...props
}: BirhausFinancialDashboardV3Props) {
  
  // BIRHAUS v3.0: Theme integration
  const theme = useBirhausThemeV3(themeName)
  
  // v3.0: Enhanced configuration with glass morphism
  const v3Configuration = {
    ...configuracion
  }
  
  // Apply v3 class names
  const v3Classes = [
    'birhaus-financial-dashboard-v3',
    'transition-all duration-500 ease-smooth',
    enableGlass ? 'backdrop-blur-lg bg-white/70 border-white/30' : '',
    floatingCards ? 'p-8 space-y-8' : 'p-6 space-y-6',
    className || ''
  ].filter(Boolean).join(' ')
  
  return (
    <div 
      className={v3Classes}
      data-birhaus-component="financial-dashboard-v3"
      data-birhaus-version="3.0"
      data-birhaus-theme={themeName}
    >
      <BirhausFinancialDashboard
        {...props}
        configuracion={v3Configuration}
        className="space-y-6"
      />
      
      {/* v3.0 Glass Morphism Overlay for Enhanced Depth */}
      {enableGlass && (
        <div 
          className="absolute inset-0 pointer-events-none rounded-3xl"
          style={{
            background: `linear-gradient(135deg, 
              rgba(255, 255, 255, 0.1) 0%, 
              rgba(255, 255, 255, 0.05) 100%)`,
            backdropFilter: 'blur(16px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            zIndex: -1
          }}
        />
      )}
    </div>
  )
}

/**
 * BIRHAUS v3.0 Enhanced Financial Metric Component
 */
export interface MetricaFinancieraV3Props extends MetricaFinanciera {
  /** v3 glass effect */
  glass?: boolean
  
  /** v3 generous spacing */
  generousSpacing?: boolean
  
  /** v3 radical border radius */
  radicalBorderRadius?: boolean
  
  /** Enable magnetic hover effect */
  magnetic?: boolean
  
  /** Enable glow effect for critical states */
  glow?: boolean
}

export function MetricaFinancieraV3({
  glass = true,
  generousSpacing = true,
  radicalBorderRadius = true,
  magnetic = true,
  glow = false,
  estado,
  ...metrica
}: MetricaFinancieraV3Props) {
  
  const getCardClasses = () => {
    const baseClasses = [
      'transition-all duration-300 ease-smooth',
      'border hover:shadow-xl'
    ]
    
    // v3 glass effect
    if (glass) {
      baseClasses.push('backdrop-blur-md bg-white/80 border-white/30')
    } else {
      baseClasses.push('bg-white border-gray-200')
    }
    
    // v3 generous spacing
    if (generousSpacing) {
      baseClasses.push('p-8')
    } else {
      baseClasses.push('p-6')
    }
    
    // v3 radical border radius
    if (radicalBorderRadius) {
      baseClasses.push('rounded-3xl')
    } else {
      baseClasses.push('rounded-lg')
    }
    
    // v3 magnetic hover effect
    if (magnetic) {
      baseClasses.push('hover:scale-105 hover:-translate-y-1 cursor-pointer')
    }
    
    // v3 glow effect for critical states
    if (glow && estado === 'critico') {
      baseClasses.push('shadow-2xl shadow-red-500/20 animate-pulse')
    }
    
    return baseClasses.join(' ')
  }
  
  return (
    <div className={getCardClasses()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {metrica.nombreEs}
        </h3>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          estado === 'saludable' ? 'bg-green-100 text-green-800' :
          estado === 'precaucion' ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {estado === 'saludable' ? 'Saludable' :
           estado === 'precaucion' ? 'Precaución' : 'Crítico'}
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="text-3xl font-bold text-gray-900">
          {metrica.unidad === 'moneda' ? `$${metrica.valor.toLocaleString()}` :
           metrica.unidad === 'porcentaje' ? `${metrica.valor}%` :
           metrica.valor.toLocaleString()}
        </div>
        
        <div className="flex items-center space-x-2">
          <div className={`flex items-center text-sm ${
            metrica.tendencia.direccion === 'subida' ? 'text-green-600' :
            metrica.tendencia.direccion === 'bajada' ? 'text-red-600' :
            'text-gray-600'
          }`}>
            <span>{metrica.tendencia.porcentaje > 0 ? '+' : ''}{metrica.tendencia.porcentaje}%</span>
          </div>
          <span className="text-sm text-gray-500">vs {metrica.tendencia.periodo}</span>
        </div>
        
        <p className="text-sm text-gray-600">{metrica.descripcionEs}</p>
      </div>
    </div>
  )
}

// Export convenience hook for v3 financial dashboard
export function useBirhausFinancialDashboardV3(
  initialConfiguration?: Partial<BirhausFinancialDashboardV3Props>
) {
  const [config, setConfig] = React.useState(initialConfiguration || {})
  
  const updateConfiguration = React.useCallback((updates: Partial<BirhausFinancialDashboardV3Props>) => {
    setConfig(prev => ({ ...prev, ...updates }))
  }, [])
  
  return {
    configuration: config,
    updateConfiguration,
    // v3-specific utilities
    enableGlassMorphism: () => updateConfiguration({ enableGlass: true, glassVariant: 'medium' }),
    enableFloatingCards: () => updateConfiguration({ floatingCards: true }),
    enableMagneticInteractions: () => updateConfiguration({ magneticInteractions: true }),
    setTheme: (theme: 'financial' | 'healthcare' | 'education') => updateConfiguration({ themeName: theme }),
    enablePerformanceMode: () => updateConfiguration({ 
      reducedMotion: true, 
      enableGlass: false, 
      magneticInteractions: false 
    }),
    isV3Enabled: true
  }
}